// 3rd party
import React, { ReactElement, useState } from 'react';
import styled from 'styled-components';
import theme from 'themes';
import { useTranslation } from 'react-i18next';

// Types
import { BusinessUnit, ChangeEvent, Machine } from 'types';
import { HelpEmailArgs, HelpEmailRequestType } from 'types/maintenance';
import { BaseSelect, Input, Typography, Button } from 'components';

//Helper
import { useAllUserMachines } from 'common/helpers/v1/machine';

//Constants
import { emailRequestTypeOptions } from 'constants/helpEmail';

// API
import { useCreateHelpEmailMutation } from 'api';
import { useHelp } from 'selectors';
import { useDispatch } from 'react-redux';
import { helpActions } from 'actions';
import { isValidEmail } from 'common/helpers/v1';
import { ToastMsg } from 'common/components/Toast';

// Styling
const Root = styled.div`
  display: block;
`;

// Components

type StateManagedSelect = {
  value: string;
  label: string;
};

const InputRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
  margin-bottom: 0rem;
  flex-grow: 1;
  align-items: center;
`;

const InputTitle = styled.div`
  margin-right: 0.25rem;
  margin-top: 0;
  margin-bottom: 0;
  align-items: center;
  padding: 0rem;
  // width: 45%;
  display: flex;
  gap: 5px;
`;

const InputItem = styled.div`
  width: 100%;
  display: flex;
  margin-bottom: 0rem;
  align-items: baseline;
  flex-direction: column;
  & > div {
    width: 100%;
  }
`;
const InputError = styled.div`
  color: ${({ theme }) => theme.colors.darkRed};
  margin-top: 0.3rem;
  font-size: 0.8rem;
`;

const InputFile = styled.div`
  width: 100%;
  display: flex;
  padding-top: 0.4rem;
`;

const Mandatory = styled.div`
  color: ${({ theme }) => theme.colors.darkRed};
  font-weight: 700;
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  padding: 1rem;
  border: 0.0625rem solid ${({ theme }) => theme.colors.lightGrey6};
  box-shadow: ${({ theme }) => theme.colors.borders.border02.shadow};
  border-radius: 0.375rem;
  font-size: 90%;
`;
const ValidationBoundary = styled.div`
  color: ${({ theme }) => theme.colors.errorRed};
`;
interface Props {
  businessUnits: BusinessUnit[];
}

type emailPropNames = 'requestType' | 'buID' | 'customerEmail' | 'message' | 'image';

const HelpEmailForm = ({ businessUnits }: Props): ReactElement => {
  const machines = useAllUserMachines();
  const helpInfo = useHelp();
  const dispatch = useDispatch();
  const { t } = useTranslation(['common']);

  const defaultHelpEmail: HelpEmailArgs = {
    requestType: HelpEmailRequestType.SelectHelp,
    customerEmail: '',
    buID: businessUnits[0].id,
    message: helpInfo?.helpMessage ? helpInfo.helpMessage : ''
  };
  const i18nEmailRequestTypesOptions = emailRequestTypeOptions.map(
    (option) => (option.label = t(option.label))
  );

  const [localHelpEmail, setLocalHelpEmail] = useState<HelpEmailArgs>(defaultHelpEmail);
  const [selectedMachine, setSelectedMachine] = useState<Machine>();
  const [uploadedFile, setUploadedFile] = useState<string>('');
  const [maxLenValidation, setMaxLenValidation] = useState<boolean>(false);

  const handleEmailChange = (
    value: string | number | HelpEmailRequestType | FormData | undefined,
    propName: emailPropNames
  ) => {
    setLocalHelpEmail({ ...localHelpEmail, [propName]: value });
  };

  const [createHelpEmail] = useCreateHelpEmailMutation();
  return (
    <Root>
      <InputRow>
        <InputItem>
          <InputTitle>
            <Typography variant="inputlabel">{t('option')} </Typography>
            <Mandatory>*</Mandatory>
          </InputTitle>

          <BaseSelect
            placeholder={t('select_dot') as string}
            options={i18nEmailRequestTypesOptions}
            variant="white"
            value={localHelpEmail?.requestType as HelpEmailRequestType}
            handleChange={(event) => handleEmailChange(event.target.value, 'requestType')}
          />
        </InputItem>
      </InputRow>
      <InputRow>
        {machines && (
          <InputItem>
            <InputTitle>
              <Typography variant="inputlabel">{t('machine')}</Typography>
              <Mandatory>*</Mandatory>
            </InputTitle>
            <BaseSelect
              placeholder={t('select_dot') as string}
              options={machines ? machines.map((m) => m.description) : ['']}
              variant="white"
              value={selectedMachine ? selectedMachine.description : ''}
              handleChange={(event) => {
                machines.forEach((m) => {
                  if (m.description === event.target.value) {
                    setSelectedMachine(m);
                    if (m.businessUnit) {
                      handleEmailChange(m.businessUnit, 'buID');
                    }
                  }
                });
              }}
            />
          </InputItem>
        )}
      </InputRow>
      <InputRow>
        {machines && (
          <InputItem>
            <InputTitle>
              <Typography variant="inputlabel">{t('machine_serial_number')}</Typography>
            </InputTitle>
            <Input
              variant={'disabled'}
              width="100%"
              value={selectedMachine?.serialNumber ? selectedMachine.serialNumber : ''}
            />
          </InputItem>
        )}
      </InputRow>
      <InputRow>
        <InputItem>
          <InputTitle>
            <Typography variant="inputlabel">{t('email')}</Typography>
            <Mandatory>*</Mandatory>
          </InputTitle>
          <Input
            type="email"
            fd
            variant="white"
            placeholder="customer@comp.com"
            value={localHelpEmail?.customerEmail}
            onChange={(event: ChangeEvent) => {
              handleEmailChange(event.target.value, 'customerEmail');
            }}
          />
          {localHelpEmail?.customerEmail && !isValidEmail(localHelpEmail?.customerEmail) && (
            <InputError>Incorrect Email Address</InputError>
          )}
        </InputItem>
      </InputRow>
      <InputRow>
        <InputItem>
          <InputTitle>
            <Typography variant="inputlabel">Comments</Typography>
            <Mandatory>*</Mandatory>
          </InputTitle>
          <StyledTextarea
            rows={3}
            value={localHelpEmail?.message}
            placeholder={'Please provide details'}
            onChange={(event) => {
              if (event.target.value.length <= 1500) {
                handleEmailChange(event.target.value, 'message');
                if (maxLenValidation) setMaxLenValidation(false);
              } else {
                setMaxLenValidation(true);
              }
            }}
          />
        </InputItem>
      </InputRow>
      {maxLenValidation && <ValidationBoundary>{t('error_max_text_len')}</ValidationBoundary>}
      <InputRow>
        <InputItem>
          <InputTitle>
            <Typography variant="inputlabel">{t('upload_file')}</Typography>
            <Typography variant="inputlabel" color={theme.colors.mediumGrey2}>
              ({t('jpeg_png')})
            </Typography>
          </InputTitle>
          <InputFile>
            <Input
              type="file"
              value={uploadedFile}
              borderVariant="none"
              onChange={(event: ChangeEvent) => {
                if (event.target.files) {
                  setUploadedFile(event.target.value);
                  const formData = new FormData();
                  formData.append('image', event.target.files[0]);
                  handleEmailChange(formData, 'image');
                }
              }}
            />
          </InputFile>
        </InputItem>
      </InputRow>

      <InputRow>
        <Button
          onClick={() => {
            dispatch({ type: helpActions.REMOVE_HELP_MESSAGE });
            if (localHelpEmail.buID == 0 && machines) {
              localHelpEmail.buID == machines[0].businessUnit;
            }
            if (!isValidEmail(localHelpEmail?.customerEmail)) {
              return ToastMsg({
                heading: 'Error',
                type: 'error',
                message: t('Incorrect Email Address')
              });
            }
            createHelpEmail({
              requestType: localHelpEmail.requestType,
              customerEmail: localHelpEmail.customerEmail,
              message: localHelpEmail.message,
              buID: localHelpEmail.buID,
              image: localHelpEmail.image,
              machine_name: selectedMachine?.description,
              selected_machine_id: selectedMachine?.serialNumber ? selectedMachine.serialNumber : 0
            })
              .unwrap()
              .then(() => {
                ToastMsg({ heading: 'Success', type: 'success', message: t('help_email_sent') });
                setLocalHelpEmail(defaultHelpEmail);
                setSelectedMachine(undefined);
                setUploadedFile('');
              })
              .catch((error) => {
                ToastMsg({ heading: 'Error', type: 'error', message: t('could_not_send_email') });
                ToastMsg({ heading: 'Error', type: 'error', message: error?.data?.detail });
              });
          }}
        >
          {t('submit')}
        </Button>
      </InputRow>
    </Root>
  );
};

export default HelpEmailForm;
